import { createEffect, createEvent, createStore, sample } from 'effector';
import { spread } from 'patronum';
import { createFeedbackPanelModel } from '../../../../components/FeedbackPanel/model';
import { ghApi } from '../../../../services/DataService';

const allowedResetPasswordEmailRegex = /@(?:ghs|ghstest)\.com$/i;

const pageMounted = createEvent<boolean>();
const setResetPasswordEmail = createEvent<string>();
const resetPasswordButtonClicked = createEvent<void>();
const feedbackPanel = createFeedbackPanelModel();

const resetPasswordFx = createEffect<{ userEmail: string }, string[]>(async ({ userEmail }) => {
  return ghApi.post('/admin/internal/password/reset', null, { params: { userEmail } }).then(res => res.data);
});

const $resetPasswordEmail = createStore('').on(setResetPasswordEmail, (_, email) => email);
const $isResetPasswordPending = resetPasswordFx.pending;

sample({
  clock: pageMounted,
  fn: () => ({
    resetPasswordEmail: '',
    resetPasswordFeedbackData: null
  }),
  target: spread({
    resetPasswordEmail: setResetPasswordEmail,
    resetPasswordFeedbackData: feedbackPanel.setFeedbackData
  })
});

sample({
  clock: resetPasswordButtonClicked,
  fn: () => null,
  target: feedbackPanel.setFeedbackData
});

sample({
  clock: resetPasswordButtonClicked,
  source: $resetPasswordEmail,
  filter: email => Boolean(email.trim()) && allowedResetPasswordEmailRegex.test(email.trim()),
  fn: email => ({ userEmail: email.trim() }),
  target: resetPasswordFx
});

sample({
  clock: resetPasswordFx.done,
  fn: ({ params, result }) => ({
    resetPasswordFeedbackData: {
      severity: result.length > 0 ? 'success' : 'warning',
      title: 'Password reset succeeded',
      message: result.length > 0 ? `Password reset for ${params.userEmail} done in ${result.join(', ')}.` : `User ${params.userEmail} doesn't exist`
    },
    resetPasswordEmail: ''
  }),
  target: spread({
    resetPasswordFeedbackData: feedbackPanel.setFeedbackData,
    resetPasswordEmail: setResetPasswordEmail
  })
});

sample({
  clock: resetPasswordFx.fail,
  fn: ({ params }) => ({
    resetPasswordFeedbackData: {
      severity: 'error',
      title: 'Password reset failed',
      message: `We couldn't reset the password for ${params.userEmail}. Please try again.`
    }
  }),
  target: spread({
    resetPasswordFeedbackData: feedbackPanel.setFeedbackData
  })
});

export const $$resetPasswordUtils = {
  $isResetPasswordPending,
  $resetPasswordEmail,
  feedbackPanel,

  pageMounted,
  setResetPasswordEmail,
  resetPasswordButtonClicked
};

export { allowedResetPasswordEmailRegex };
